import React, { ChangeEvent } from 'react';
import { Box, Typography, Switch, FormControlLabel } from '@mui/material';
import { useFilters } from '../../state/hooks/useFilters';
import { useFilteredBeers, useActiveTab } from '../../state/hooks/useBeerSelectors';
import {
  buildFilterAppliedEvent,
  buildFilterClearedEvent,
  useAnalyticsService
} from '../../services/analytics';

interface BeerStatsAndSettingsProps {
  // No props needed as we'll use context hooks
}

/**
 * BeerStatsAndSettings - A component that displays beer count and availability toggle
 * Follows SOLID principles by having a single responsibility
 */
const BeerStatsAndSettings: React.FC<BeerStatsAndSettingsProps> = () => {
  const analyticsService = useAnalyticsService();

  // Get filtered beers directly from the focused hook
  const filteredBeers = useFilteredBeers();
  
  // Get active tab from the focused hook
  const activeTab = useActiveTab();
  
  // Whether we're on the "My Drinks" tab
  const isMyDrinksTab = activeTab === '1';
  
  const { 
    filters: {
      availabilityFilter,
      showOnlyFavorites
    },
    setAvailabilityFilter
  } = useFilters();


  // Handle availability toggle
  const handleAvailabilityToggle = (event: ChangeEvent<HTMLInputElement>): void => {
    const isChecked = event.target.checked;
    
    if (isChecked) {
      setAvailabilityFilter('available');
      analyticsService.trackEvent(buildFilterAppliedEvent('availability', 'available'));
    } else {
      setAvailabilityFilter('');
      analyticsService.trackEvent(buildFilterClearedEvent('availability'));
    }
  };

  // Get dynamic text for My Beers tab
  const getMyBeersText = () => {
    if (showOnlyFavorites) {
      // Tasting List toggle is active
      return `You have ${filteredBeers.length} ${filteredBeers.length === 1 ? 'beer' : 'beers'} in your tasting list`;
    } else {
      // Tried Beers toggle is active
      return `You've tried ${filteredBeers.length} ${filteredBeers.length === 1 ? 'beer' : 'beers'}`;
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: isMyDrinksTab ? 'center' : 'space-between', 
        alignItems: 'center',
        width: '100%',
        mb: 1
      }}
    >
      {/* Beer count - centered for My Beers, left for All Beers */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="body1" fontWeight="medium" sx={{ 
          textAlign: isMyDrinksTab ? 'center' : 'left',
          color: 'var(--primary-text-colour)'
        }}>
          {isMyDrinksTab ? getMyBeersText() : `${filteredBeers.length} ${filteredBeers.length === 1 ? 'beer' : 'beers'} found`}
        </Typography>
      </Box>
      
      {/* Toggle switch - right side - only show for All Beers tab */}
      {!isMyDrinksTab && (
        <Box sx={{ flexShrink: 0 }}>
          <FormControlLabel
            control={
              <Switch 
                checked={availabilityFilter === 'available'}
                onChange={handleAvailabilityToggle}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: 'white',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ 
                fontSize: '0.875rem',
                color: 'var(--primary-text-colour)'
              }}>
                Show only available
              </Typography>
            }
            sx={{ mr: 0 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default BeerStatsAndSettings;
